/**
 * CompanyFormDetails — wrapper moderne autour de <details> HTML5.
 *
 * Pattern UX commun établi 9.P.2.R1 — réutilisable Create + Edit (et R2/R3
 * pour Brokers/Guarantees/Settings forms si besoin).
 *
 * Préserve la sémantique HTML5 native `<details>` (Q5 PO 9.P.2 = conserver,
 * a11y native clavier Enter/Space pour ouvrir/fermer) en y appliquant un
 * style moderne :
 *   - summary sans triangle natif (`[&::-webkit-details-marker]:hidden`)
 *   - chevron icône qui rotate `[details[open]_&]:rotate-180`
 *   - hover state sur summary (`hover:bg-slate-50`)
 *   - body avec `border-t border-slate-100` séparation visuelle
 *   - `scroll-mt-32` pour anchor-link scroll-to ne pas être masqué par
 *     le sticky page header parent
 *
 * a11y :
 *   - `<details>` natif HTML5 (Enter/Space ouvre/ferme via summary)
 *   - focus-visible:ring-brand-500 sur summary
 *   - `id` propagé pour anchor-link consommé par CompanySectionNav
 */

import { type SyntheticEvent } from "react";

type Props = {
  readonly id: string;
  readonly title: string;
  readonly open: boolean;
  readonly onToggle?: (e: SyntheticEvent<HTMLDetailsElement>) => void;
  readonly children: React.ReactNode;
};

export function CompanyFormDetails({
  id,
  title,
  open,
  onToggle,
  children,
}: Props) {
  return (
    <details
      id={id}
      open={open}
      onToggle={onToggle}
      className="scroll-mt-32 rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <summary className="flex cursor-pointer items-center justify-between rounded-xl px-5 py-3.5 text-sm font-semibold text-slate-900 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden">
        <span>{title}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          className="ml-2 shrink-0 text-slate-400 transition-transform motion-reduce:transition-none [details[open]_&]:rotate-180"
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </summary>
      <div className="border-t border-slate-100 px-5 py-5">{children}</div>
    </details>
  );
}
